import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSpinner, faTimesCircle, faChartLine, faDollarSign, faSync, faArrowUp, faArrowDown, faTimes } from '@fortawesome/free-solid-svg-icons';
import { assetApi } from '../api/assetApi';
import { marketDataApi } from '../api/marketDataApi';
import { orderApi } from '../api/orderApi';
import { accountApi } from '../api/accountApi';
import { schwabApi } from '../api/schwabApi';
import HistoricalBarsChart from '../components/HistoricalBarsChart';
import CorporateActions from '../components/CorporateActions';
import TickerNews from '../components/TickerNews';
import MutualFundPanel from '../components/MutualFundPanel';
import OrderForm from '../components/OrderForm';
import MultiLegOrderForm from '../components/MultiLegOrderForm';
import { useRisk } from '../contexts/RiskContext';

function mapSchwabOverviewToAsset(overview, symbol) {
  const inst = overview?.instrument || {};
  const type = String(inst.assetType || 'MUTUAL_FUND').toUpperCase();
  return {
    id: `schwab:${String(inst.symbol || symbol).toUpperCase()}`,
    symbol: String(inst.symbol || symbol).toUpperCase(),
    name: inst.name || inst.description || String(symbol || '').toUpperCase(),
    exchange: inst.exchange || null,
    class: type === 'MUTUAL_FUND' ? 'mutual_fund' : String(type || 'us_equity').toLowerCase(),
    assetType: type,
    status: 'active',
    tradable: true,
    marginable: false,
    shortable: false,
    easy_to_borrow: false,
    fractionable: type === 'MUTUAL_FUND' || type === 'EQUITY' || type === 'ETF',
    provider: 'schwab',
    source: 'schwab',
    cusip: inst.cusip || null,
  };
}

function mapSchwabQuoteToUi(q) {
  if (!q || typeof q !== 'object') return null;
  return {
    bidPrice: q.bidPrice ?? null,
    askPrice: q.askPrice ?? null,
    bidSize: q.bidSize ?? null,
    askSize: q.askSize ?? null,
    lastPrice: q.lastPrice ?? q.closePrice ?? q.nav ?? null,
    netChange: q.netChange ?? null,
    netPercentChange: q.netPercentChange ?? null,
    provider: 'schwab',
  };
}

const AssetDetailsPage = () => {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { checkOrder } = useRisk();
  const [asset, setAsset] = useState(null);
  const [quote, setQuote] = useState(null);
  const [watchlistMarketData, setWatchlistMarketData] = useState(null);
  const [marketDataSummary, setMarketDataSummary] = useState(null);
  const [position, setPosition] = useState(null); // primary (first) for quick actions
  const [venuePositions, setVenuePositions] = useState([]); // alpaca + schwab rows
  const [closeTarget, setCloseTarget] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [loadingMarketData, setLoadingMarketData] = useState(false);
  const [loadingPosition, setLoadingPosition] = useState(false);
  const [closingPosition, setClosingPosition] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [error, setError] = useState(null);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [orderSide, setOrderSide] = useState('buy');
  const [orderTicketTab, setOrderTicketTab] = useState('stock'); // stock | bracket | mleg
  const [orderMessage, setOrderMessage] = useState(null);
  const [yahooData, setYahooData] = useState(null);
  const [loadingYahoo, setLoadingYahoo] = useState(false);
  const [yahooError, setYahooError] = useState(null);
  const [schwabOverview, setSchwabOverview] = useState(null);

  // Bracket ticket state
  const [brktQty, setBrktQty] = useState('1');
  const [brktSide, setBrktSide] = useState('buy');
  const [brktTp, setBrktTp] = useState('');
  const [brktSl, setBrktSl] = useState('');
  const [brktEntry, setBrktEntry] = useState('market'); // market | limit
  const [brktLimit, setBrktLimit] = useState('');
  const [brktSubmitting, setBrktSubmitting] = useState(false);

  const isMutualFund =
    String(asset?.assetType || '').toUpperCase() === 'MUTUAL_FUND' ||
    String(asset?.class || '').toLowerCase() === 'mutual_fund';
  const isSchwabAsset = asset?.provider === 'schwab' || asset?.source === 'schwab';

  useEffect(() => {
    // Check if we have market data from watchlist navigation
    if (location.state?.marketData) {
      setWatchlistMarketData(location.state.marketData);
      setMarketDataSummary(location.state.marketData);
    }
    loadAsset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, location.state]);

  useEffect(() => {
    if (asset) {
      loadLogo();

      if (isMutualFund || isSchwabAsset) {
        // Funds: Schwab fundamentals/quote — skip equity Yahoo/Alpaca-heavy paths when possible
        if (!schwabOverview) {
          loadSchwabOverview();
        }
        loadPosition();
      } else {
        loadYahooFinanceData();
        if (asset.tradable) {
          if (!watchlistMarketData) {
            loadLatestQuote();
          }
          if (!marketDataSummary) {
            loadMarketDataSummary();
          }
          loadPosition();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset, watchlistMarketData, marketDataSummary]);

  const loadSchwabOverview = async () => {
    if (!symbol) return;
    try {
      const response = await marketDataApi.getInstrumentOverview(symbol);
      const overview = response?.data || response;
      setSchwabOverview(overview || null);
      const mappedQuote = mapSchwabQuoteToUi(overview?.quote);
      if (mappedQuote) setQuote(mappedQuote);
      // Seed a simple day summary from Schwab quote when we lack Alpaca bars
      if (overview?.quote && !marketDataSummary) {
        const q = overview.quote;
        const last = q.lastPrice ?? q.closePrice ?? q.mark ?? null;
        const open = q.openPrice ?? q.open ?? null;
        const high = q.highPrice ?? q.high ?? null;
        const low = q.lowPrice ?? q.low ?? null;
        if (last != null || open != null) {
          setMarketDataSummary({
            open: open != null ? Number(open) : null,
            high: high != null ? Number(high) : null,
            low: low != null ? Number(low) : null,
            close: last != null ? Number(last) : null,
            volume: q.totalVolume ?? q.volume ?? null,
            provider: 'schwab',
          });
        }
      }
    } catch (err) {
      console.warn('Schwab overview unavailable:', err.message);
    }
  };

  const loadAsset = async () => {
    try {
      setLoading(true);
      setError(null);
      setSchwabOverview(null);
      try {
        const response = await assetApi.getAssetBySymbol(symbol);
        setAsset(response.data);
        return;
      } catch (alpacaErr) {
        // Mutual funds (and some OTC names) live on Schwab, not Alpaca's catalog
        const status = alpacaErr.response?.status;
        if (status !== 404 && status !== 400) {
          throw alpacaErr;
        }
      }

      const overviewRes = await marketDataApi.getInstrumentOverview(symbol);
      const overview = overviewRes?.data || overviewRes;
      if (!overview?.instrument?.symbol && !overview?.instrument?.description) {
        throw new Error(`Asset with symbol ${String(symbol || '').toUpperCase()} not found`);
      }
      const mapped = mapSchwabOverviewToAsset(overview, symbol);
      setSchwabOverview(overview);
      setAsset(mapped);
      const mappedQuote = mapSchwabQuoteToUi(overview?.quote);
      if (mappedQuote) setQuote(mappedQuote);
    } catch (err) {
      setAsset(null);
      setError(err.response?.data?.error || err.message || 'Failed to load asset');
    } finally {
      setLoading(false);
    }
  };

  const loadLogo = async () => {
    if (!symbol) return;
    
    try {
      const response = await marketDataApi.getLogo(symbol);
      setLogoUrl(response.logo || null);
    } catch (err) {
      console.error('Failed to load logo:', err);
      // Don't show error for logo, just log it
      setLogoUrl(null);
    }
  };

  const loadYahooFinanceData = async () => {
    if (!symbol) return;
    
    try {
      setLoadingYahoo(true);
      setYahooError(null);
      const response = await marketDataApi.yahoo.getQuoteSummary(symbol);
      console.log('Yahoo Finance response:', response);
      
      // The response structure is: { success: true, symbol: "...", data: {...} }
      // The data object contains the modules as keys (summaryProfile, price, etc.)
      console.log('Full Yahoo Finance response:', JSON.stringify(response, null, 2));
      
      if (response.success && response.data) {
        console.log('Yahoo Finance data received:', response.data);
        console.log('Available modules:', Object.keys(response.data));
        console.log('Price module:', response.data.price);
        console.log('SummaryDetail module:', response.data.summaryDetail);
        console.log('SummaryProfile module:', response.data.summaryProfile);
        
        // Check if we have any actual data in the modules
        const hasData = Object.keys(response.data).some(key => {
          const module = response.data[key];
          return module && typeof module === 'object' && Object.keys(module).length > 0;
        });
        
        console.log('Has data in modules:', hasData);
        
        if (hasData) {
          setYahooData(response.data);
        } else {
          console.warn('Yahoo Finance data exists but modules are empty');
          setYahooData(null);
          setYahooError('Yahoo Finance returned empty data');
        }
      } else {
        console.warn('Yahoo Finance response missing data:', response);
        setYahooData(null);
        setYahooError('No data returned from Yahoo Finance');
      }
    } catch (err) {
      console.error('Failed to load Yahoo Finance data:', err);
      console.error('Error details:', err.response?.data || err.message);
      setYahooData(null);
      setYahooError(err.response?.data?.error || err.message || 'Failed to load Yahoo Finance data');
    } finally {
      setLoadingYahoo(false);
    }
  };

  const loadLatestQuote = async () => {
    if (!asset || !asset.tradable) return;

    try {
      setLoadingQuote(true);
      if (isSchwabAsset || isMutualFund) {
        const response = await marketDataApi.getInstrumentOverview(symbol);
        const overview = response?.data || response;
        setSchwabOverview(overview || schwabOverview);
        const mappedQuote = mapSchwabQuoteToUi(overview?.quote);
        setQuote(mappedQuote);
        return;
      }
      const response = await marketDataApi.getQuote(symbol);
      setQuote(response.data);
    } catch (err) {
      console.error('Failed to load quote:', err);
      setQuote(null);
    } finally {
      setLoadingQuote(false);
    }
  };

  const loadMarketDataSummary = async () => {
    if (!asset || !asset.tradable) return;
    
    try {
      setLoadingMarketData(true);
      // Fetch latest bar data (1 day timeframe, 1 bar) to get OHLCV
      const response = await marketDataApi.getBars(symbol, {
        timeframe: '1Day',
        limit: 1,
      });
      
      if (response.success && response.data && response.data.length > 0) {
        const bar = response.data[0];
        // Also get quote for current price
        try {
          const quoteResponse = await marketDataApi.getQuote(symbol);
          const currentPrice = quoteResponse.data?.bidPrice && quoteResponse.data?.askPrice
            ? (quoteResponse.data.bidPrice + quoteResponse.data.askPrice) / 2
            : bar.close;
          
          setMarketDataSummary({
            currentPrice: currentPrice,
            open: bar.open,
            high: bar.high,
            low: bar.low,
            close: bar.close,
            volume: bar.volume,
          });
        } catch (quoteErr) {
          // If quote fails, use close price as current
          setMarketDataSummary({
            currentPrice: bar.close,
            open: bar.open,
            high: bar.high,
            low: bar.low,
            close: bar.close,
            volume: bar.volume,
          });
        }
      }
    } catch (err) {
      console.error('Failed to load market data summary:', err);
      // Don't show error, just log it
    } finally {
      setLoadingMarketData(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(value);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value.raw != null) return formatNumber(value.raw);
    if (isNaN(value)) return 'N/A';
    if (Math.abs(value) >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
    if (Math.abs(value) >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    if (Math.abs(value) >= 1e3) return `$${(value / 1e3).toFixed(2)}K`;
    return `$${Number(value).toFixed(2)}`;
  };

  const yahooRaw = (value) => {
    if (value == null) return null;
    if (typeof value === 'object') {
      if (value.raw != null && Number.isFinite(Number(value.raw))) return Number(value.raw);
      return null;
    }
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    return null;
  };

  const yahooFmt = (value, { percent = false, digits = 2 } = {}) => {
    if (value == null) return null;
    if (typeof value === 'object') {
      if (value.fmt != null && value.fmt !== '') return value.fmt;
      if (value.raw != null && Number.isFinite(Number(value.raw))) {
        return yahooFmt(Number(value.raw), { percent, digits });
      }
      return null;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      if (percent) return `${(value * 100).toFixed(digits)}%`;
      return value.toFixed(digits);
    }
    if (typeof value === 'string' && value) return value;
    return null;
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  /** Alpaca/Schwab often return P/L % as a fraction (0.05 = 5%). */
  const plpcAsPercent = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return Math.abs(n) <= 1 ? n * 100 : n;
  };

  const calculateChange = (current, previous) => {
    if (!current || !previous || isNaN(current) || isNaN(previous)) return null;
    return ((current - previous) / previous) * 100;
  };

  const loadPosition = async () => {
    if (!symbol) return;

    try {
      setLoadingPosition(true);
      const sym = String(symbol).toUpperCase();
      const [alpacaRes, schwabRes] = await Promise.all([
        accountApi.getPositions().catch(() => ({ data: [] })),
        schwabApi.getPositions().catch(() => ({ data: [] })),
      ]);

      const next = [];
      const alpacaMatch = (alpacaRes.data || []).find(
        (pos) => String(pos.symbol || '').toUpperCase() === sym
      );
      if (alpacaMatch) {
        next.push({ ...alpacaMatch, venue: 'alpaca' });
      }

      const schwabMatches = (schwabRes.data || []).filter(
        (pos) => String(pos.symbol || '').toUpperCase() === sym
      );
      for (const pos of schwabMatches) {
        next.push({ ...pos, venue: 'schwab' });
      }

      setVenuePositions(next);
      setPosition(next[0] || null);
    } catch (err) {
      console.error('Failed to load position:', err);
      setVenuePositions([]);
      setPosition(null);
    } finally {
      setLoadingPosition(false);
    }
  };

  const resetOrderForm = () => {
    setShowOrderForm(false);
    setOrderMessage(null);
    setOrderTicketTab('stock');
    setBrktSubmitting(false);
  };

  const handleClosePosition = (target = null) => {
    const row = target || position;
    if (!row) return;
    setCloseTarget(row);
    setOrderMessage(null);
    setShowCloseDialog(true);
  };

  const confirmClosePosition = async () => {
    const row = closeTarget || position;
    if (!row) return;

    try {
      setClosingPosition(true);
      const sym = String(symbol).toUpperCase();
      if (row.venue === 'schwab') {
        const accountNumber = row.accountNumber;
        if (!accountNumber) throw new Error('Schwab account missing on position');
        const qtyAbs = Math.abs(Number(row.qty) || 0);
        if (!(qtyAbs > 0)) throw new Error('Invalid Schwab position quantity');
        const instruction = row.side === 'short' ? 'BUY_TO_COVER' : 'SELL';
        await schwabApi.placeOrder({
          accountNumber: String(accountNumber),
          symbol: sym,
          instruction,
          quantity: qtyAbs,
          orderType: 'MARKET',
          duration: 'DAY',
          session: 'NORMAL',
          assetType: row.assetType || asset?.assetType || 'EQUITY',
        });
      } else {
        await accountApi.closePosition(sym);
      }
      await loadPosition();
      setShowCloseDialog(false);
      setCloseTarget(null);
      setOrderMessage({
        type: 'success',
        text: `${sym} flattened on ${row.venue === 'schwab' ? 'Schwab' : 'Alpaca'}.`,
      });
      setTimeout(() => setOrderMessage(null), 5000);
    } catch (err) {
      setOrderMessage({
        type: 'error',
        text: err.response?.data?.error || err.message || 'Failed to close position',
      });
    } finally {
      setClosingPosition(false);
    }
  };

  const seedBracketFromQuote = (side) => {
    const last =
      quote?.lastPrice ??
      (quote?.bidPrice != null && quote?.askPrice != null
        ? (Number(quote.bidPrice) + Number(quote.askPrice)) / 2
        : quote?.askPrice || quote?.bidPrice);
    if (last == null || !Number.isFinite(Number(last))) return;
    const mid = Number(last);
    setBrktLimit(mid.toFixed(2));
    if (side === 'sell') {
      setBrktTp((mid * 0.98).toFixed(2));
      setBrktSl((mid * 1.02).toFixed(2));
    } else {
      setBrktTp((mid * 1.02).toFixed(2));
      setBrktSl((mid * 0.98).toFixed(2));
    }
  };

  const handleQuickOrder = (side) => {
    setOrderSide(side);
    setBrktSide(side);
    setOrderMessage(null);
    setOrderTicketTab('stock');
    seedBracketFromQuote(side);
    if (position?.qty) {
      const absQty = Math.abs(Number(position.qty));
      if (Number.isFinite(absQty) && absQty > 0) setBrktQty(String(absQty));
    }
    setShowOrderForm(true);
  };

  const handleBracketSubmit = async (e) => {
    e.preventDefault();
    const qty = parseFloat(brktQty);
    const tp = parseFloat(brktTp);
    const sl = parseFloat(brktSl);
    if (!Number.isFinite(qty) || qty <= 0) {
      setOrderMessage({ type: 'error', text: 'Enter a valid bracket quantity' });
      return;
    }
    if (!Number.isFinite(tp) || tp <= 0 || !Number.isFinite(sl) || sl <= 0) {
      setOrderMessage({ type: 'error', text: 'Enter take-profit and stop-loss prices' });
      return;
    }
    if (brktSide === 'buy' && tp <= sl) {
      setOrderMessage({ type: 'error', text: 'Long bracket needs TP > SL' });
      return;
    }
    if (brktSide === 'sell' && tp >= sl) {
      setOrderMessage({ type: 'error', text: 'Short bracket needs TP < SL' });
      return;
    }
    if (brktEntry === 'limit' && (!brktLimit || !Number.isFinite(parseFloat(brktLimit)))) {
      setOrderMessage({ type: 'error', text: 'Enter a limit entry price' });
      return;
    }

    try {
      setBrktSubmitting(true);
      setOrderMessage(null);
      const mid =
        quote?.lastPrice ??
        (quote?.bidPrice != null && quote?.askPrice != null
          ? (Number(quote.bidPrice) + Number(quote.askPrice)) / 2
          : null);
      const estimatedNotional = mid != null ? qty * Number(mid) : null;
      const risk = await checkOrder({
        symbol: String(symbol).toUpperCase(),
        qty,
        side: brktSide,
        type: brktEntry === 'limit' ? 'limit' : 'market',
        estimatedNotional,
      });
      if (risk?.blocks?.length) {
        setOrderMessage({ type: 'error', text: `Risk block: ${risk.blocks.join(' · ')}` });
        return;
      }
      if (risk?.warnings?.length) {
        const ok = window.confirm(
          `Risk warnings:\n\n• ${risk.warnings.join('\n• ')}\n\nSubmit bracket anyway?`
        );
        if (!ok) return;
      }

      await orderApi.submitOtoOrder({
        symbol: String(symbol).toUpperCase(),
        qty,
        side: brktSide,
        type: brktEntry === 'limit' ? 'limit' : 'market',
        limitPrice: brktEntry === 'limit' ? parseFloat(brktLimit) : undefined,
        timeInForce: 'gtc',
        takeProfitLimitPrice: tp,
        stopLossStopPrice: sl,
        orderClass: 'bracket',
      });

      setOrderMessage({
        type: 'success',
        text: `Bracket ${brktSide.toUpperCase()} ${qty} ${String(symbol).toUpperCase()} · TP ${tp} / SL ${sl}`,
      });
      await loadPosition();
      setTimeout(() => {
        setShowOrderForm(false);
        setOrderMessage(null);
      }, 2200);
    } catch (err) {
      setOrderMessage({
        type: 'error',
        text: err.response?.data?.error || err.message || 'Bracket order failed',
      });
    } finally {
      setBrktSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="flex-1 container mx-auto px-4 lg:px-6 py-3">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} className="text-2xl text-slate-600 animate-spin mb-2" />
            <p className="text-sm text-gray-600">Loading asset details...</p>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="flex-1 container mx-auto px-4 lg:px-6 py-3">
        <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
          <div className="text-center">
            <FontAwesomeIcon icon={faTimesCircle} className="text-3xl text-red-500 mb-2" />
            <h2 className="text-lg font-bold text-gray-800 mb-1">Asset Not Found</h2>
            <p className="text-sm text-gray-600 mb-3">{error}</p>
            <div className="flex justify-center space-x-2">
              <button
                onClick={() => navigate(-1)}
                className="px-3 py-1.5 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors text-xs font-medium"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />
                Go Back
              </button>
              <Link
                to="/settings"
                className="px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-xs font-medium"
              >
                Settings / Schwab
              </Link>
            </div>
            <p className="text-[11px] text-gray-500 mt-3 max-w-md mx-auto">
              Mutual funds are loaded from Schwab when they are not in the Alpaca catalog. Connect Schwab in Settings if this symbol should resolve.
            </p>
          </div>
        </div>
      </main>
    );
  }

  if (!asset) return null;

  const keyStats = yahooData?.defaultKeyStatistics || {};
  const summaryDetail = yahooData?.summaryDetail || {};
  const priceMod = yahooData?.price || {};
  const keyMetricRows = [
    {
      label: 'Market Cap',
      value: (() => {
        const raw = yahooRaw(keyStats.marketCap) ?? yahooRaw(priceMod.marketCap) ?? yahooRaw(summaryDetail.marketCap);
        return raw != null ? formatNumber(raw) : null;
      })(),
    },
    {
      label: 'P/E Ratio',
      value: yahooFmt(keyStats.trailingPE) || yahooFmt(summaryDetail.trailingPE),
    },
    {
      label: 'Forward P/E',
      value: yahooFmt(keyStats.forwardPE) || yahooFmt(summaryDetail.forwardPE),
    },
    {
      label: 'Dividend Yield',
      value: yahooFmt(summaryDetail.dividendYield, { percent: true, digits: 2 }) || yahooFmt(keyStats.yield, { percent: true, digits: 2 }),
    },
    {
      label: 'EPS (TTM)',
      value: (() => {
        const raw = yahooRaw(keyStats.trailingEps);
        if (raw == null) {
          const fmt = yahooFmt(keyStats.trailingEps);
          return fmt ? (fmt.startsWith('$') ? fmt : `$${fmt}`) : null;
        }
        return `$${raw.toFixed(2)}`;
      })(),
    },
    {
      label: 'Price to Book',
      value: yahooFmt(keyStats.priceToBook),
    },
    {
      label: 'Beta',
      value: yahooFmt(keyStats.beta) || yahooFmt(summaryDetail.beta),
    },
    {
      label: '52W Range',
      value: (() => {
        const low = yahooRaw(summaryDetail.fiftyTwoWeekLow) ?? yahooFmt(summaryDetail.fiftyTwoWeekLow);
        const high = yahooRaw(summaryDetail.fiftyTwoWeekHigh) ?? yahooFmt(summaryDetail.fiftyTwoWeekHigh);
        if (low == null || high == null) return null;
        const lowStr = typeof low === 'number' ? `$${low.toFixed(2)}` : low;
        const highStr = typeof high === 'number' ? `$${high.toFixed(2)}` : high;
        return `${lowStr} – ${highStr}`;
      })(),
    },
  ].filter((row) => row.value != null && row.value !== '' && row.value !== 'N/A');

  const earningsChart = yahooData?.earnings?.earningsChart || {};
  const calEarnings = yahooData?.calendarEvents?.earnings || {};
  const nextEarningsDate = (() => {
    const rawDates = [
      ...(Array.isArray(calEarnings.earningsDate) ? calEarnings.earningsDate : []),
      ...(Array.isArray(earningsChart.earningsDate) ? earningsChart.earningsDate : []),
    ];
    const parsed = rawDates
      .map((d) => new Date(d))
      .filter((d) => !Number.isNaN(d.getTime()))
      .sort((a, b) => a - b);
    if (!parsed.length) return null;
    const soon = Date.now() - 12 * 60 * 60 * 1000;
    return parsed.find((d) => d.getTime() >= soon) || parsed[parsed.length - 1];
  })();
  const nextEarningsIsEstimate = Boolean(
    calEarnings.isEarningsDateEstimate ?? earningsChart.isEarningsDateEstimate
  );
  const nextEpsEstimate =
    yahooRaw(calEarnings.earningsAverage) ??
    yahooRaw(earningsChart.currentQuarterEstimate);
  const formatEarningsDay = (d) => {
    if (!d) return null;
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };
  const daysUntilEarnings = nextEarningsDate
    ? Math.ceil((nextEarningsDate.getTime() - Date.now()) / (24 * 60 * 60 * 1000))
    : null;
  const quarterlyEarnings = [...(earningsChart.quarterly || [])]
    .map((q) => ({
      ...q,
      sortMs: new Date(q.reportedDate || q.periodEndDate || 0).getTime() || 0,
      actualN: yahooRaw(q.actual),
      estimateN: yahooRaw(q.estimate),
      surpriseN:
        yahooRaw(q.surprisePct) ??
        (q.surprisePct != null && Number.isFinite(Number(q.surprisePct))
          ? Number(q.surprisePct)
          : null),
    }))
    .sort((a, b) => b.sortMs - a.sortMs)
    .slice(0, 4);
  const yearlyFinancials = [...(yahooData?.earnings?.financialsChart?.yearly || [])]
    .sort((a, b) => Number(b.date) - Number(a.date))
    .slice(0, 3);
  const hasEarningsBlock =
    nextEarningsDate || quarterlyEarnings.length > 0 || yearlyFinancials.length > 0;

  const majorHolders = yahooData?.majorHoldersBreakdown || {};
  const insiderTxRows = [...(yahooData?.insiderTransactions?.transactions || [])]
    .map((tx, idx) => {
      const shares = yahooRaw(tx.shares);
      const value = yahooRaw(tx.value);
      const startRaw = yahooRaw(tx.startDate);
      const startMs =
        startRaw != null
          ? startRaw < 1e12
            ? startRaw * 1000
            : startRaw
          : tx.startDate?.fmt
            ? Date.parse(tx.startDate.fmt)
            : 0;
      const text = String(tx.transactionText || '').toLowerCase();
      let side = 'Other';
      if (/\bsale\b|\bsold\b|\bdispose\b|\bsell\b/.test(text)) side = 'Sell';
      else if (/\bpurchase\b|\bbuy\b|\bacquisit/.test(text)) side = 'Buy';
      return {
        id: `${tx.filerName || 'x'}-${startMs || idx}-${shares || idx}`,
        dateLabel:
          tx.startDate?.fmt ||
          (startMs
            ? new Date(startMs).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: '2-digit',
              })
            : '—'),
        sortMs: startMs || 0,
        filer: tx.filerName || '—',
        relation: tx.filerRelation || '—',
        text: tx.transactionText || '—',
        shares,
        value: value != null ? Math.abs(Number(value)) : null,
        side,
      };
    })
    .sort((a, b) => b.sortMs - a.sortMs)
    .slice(0, 15);

  const insiderHolderRows = [...(yahooData?.insiderHolders?.holders || [])]
    .map((h, idx) => ({
      id: `${h.name || 'h'}-${idx}`,
      name: h.name || '—',
      relation: h.relation || '—',
      detail: h.transactionDescription || '—',
      dateLabel: h.latestTransDate?.fmt || '—',
      position: yahooRaw(h.positionDirect) ?? yahooRaw(h.positionIndirect),
    }))
    .slice(0, 8);

  const institutionRows = [...(yahooData?.institutionOwnership?.ownershipList || [])]
    .map((row, idx) => ({
      id: `${row.organization || 'org'}-${idx}`,
      org: row.organization || '—',
      pct: yahooRaw(row.pctHeld),
      shares: yahooRaw(row.position),
      value: yahooRaw(row.value),
      reportDate: row.reportDate?.fmt || '—',
    }))
    .sort((a, b) => (b.pct || 0) - (a.pct || 0))
    .slice(0, 10);

  const hasOwnershipBlock =
    Boolean(yahooFmt(majorHolders.insidersPercentHeld, { percent: true })) ||
    Boolean(yahooFmt(majorHolders.institutionsPercentHeld, { percent: true })) ||
    insiderTxRows.length > 0 ||
    insiderHolderRows.length > 0 ||
    institutionRows.length > 0;

  return (
    <main className="flex-1 w-full max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-6 py-3">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="mb-2 flex items-center text-slate-700 hover:text-slate-800 text-sm font-medium transition-colors"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="mr-1 text-xs" />
        Back
      </button>

      {/* Asset Header */}
      <div className="bg-white rounded-lg shadow-md p-3 mb-3 border border-gray-100">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              {logoUrl && (
                <img 
                  src={logoUrl} 
                  alt={`${asset.symbol} logo`}
                  className="w-10 h-10 rounded-lg object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <h1 className="text-2xl font-bold text-gray-800">{asset.symbol}</h1>
              {isMutualFund && (
                <span className="px-2 py-0.5 bg-amber-100 text-amber-900 text-xs font-semibold rounded">
                  Mutual Fund
                </span>
              )}
              {isSchwabAsset && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                  Schwab
                </span>
              )}
              {asset.status === 'active' && (
                <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-semibold rounded">
                  Active
                </span>
              )}
            </div>
            {asset.name && (
              <p className="text-sm text-gray-600">{asset.name}</p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {asset.exchange && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide bg-slate-100 text-slate-700 border border-slate-200">
                  {asset.exchange}
                </span>
              )}
              {asset.class && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-50 text-slate-600 border border-slate-200">
                  {String(asset.class).replace(/_/g, ' ')}
                </span>
              )}
              {[
                asset.tradable && 'Tradable',
                asset.marginable && 'Marginable',
                asset.shortable && 'Shortable',
                asset.easy_to_borrow && 'ETB',
                asset.fractionable && 'Fractionable',
              ]
                .filter(Boolean)
                .map((label) => (
                  <span
                    key={label}
                    className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
                  >
                    {label}
                  </span>
                ))}
              {nextEarningsDate && (
                <span
                  className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200"
                  title={nextEarningsIsEstimate ? 'Estimated earnings date' : 'Scheduled earnings date'}
                >
                  Earn {nextEarningsDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  {daysUntilEarnings != null && daysUntilEarnings >= 0 ? ` · ${daysUntilEarnings}d` : ''}
                  {nextEarningsIsEstimate ? ' est' : ''}
                </span>
              )}
            </div>
          </div>
          
          {/* Quick Buy/Sell — Close stays a one-click market flatten when you have a position */}
          {asset.tradable && (
            <div className="flex items-center space-x-2 ml-3">
              <button
                onClick={() => handleQuickOrder('buy')}
                className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs font-semibold flex items-center space-x-1"
              >
                <FontAwesomeIcon icon={faArrowUp} className="text-xs" />
                <span>Buy</span>
              </button>
              <button
                onClick={() => handleQuickOrder('sell')}
                className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs font-semibold flex items-center space-x-1"
              >
                <FontAwesomeIcon icon={faArrowDown} className="text-xs" />
                <span>Sell</span>
              </button>
              {venuePositions.length === 1 && (
                <button
                  onClick={() => handleClosePosition(venuePositions[0])}
                  disabled={closingPosition}
                  title={`Market close entire ${venuePositions[0].side} ${venuePositions[0].venue === 'schwab' ? 'Schwab' : 'Alpaca'} position (${Math.abs(venuePositions[0].qty)} shares)`}
                  className="px-3 py-1.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-xs font-semibold flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {closingPosition ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
                      <span>Closing...</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faTimes} className="text-xs" />
                      <span>Close</span>
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Position Information — Alpaca and/or Schwab */}
        {venuePositions.length > 0 && (
          <div className="mb-3 bg-white rounded-lg shadow-md p-3 border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-bold text-gray-800 flex items-center">
                <FontAwesomeIcon icon={faChartLine} className="mr-1.5 text-xs text-gray-600" />
                Your Position{venuePositions.length > 1 ? 's' : ''}
              </h3>
              <button
                onClick={loadPosition}
                disabled={loadingPosition}
                className="px-2 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
              >
                {loadingPosition ? (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSync} className="mr-1 text-xs" />
                    Refresh
                  </>
                )}
              </button>
            </div>
            <div className="space-y-3">
              {venuePositions.map((row, idx) => {
                const plPct = plpcAsPercent(row.unrealizedPlpc);
                const venueLabel = row.venue === 'schwab' ? 'Schwab' : 'Alpaca';
                const venueKey =
                  row.venue === 'schwab'
                    ? `schwab-${row.accountNumber || idx}`
                    : `alpaca-${idx}`;
                return (
                  <div
                    key={venueKey}
                    className={`${idx > 0 ? 'pt-3 border-t border-gray-200' : ''}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${
                            row.venue === 'schwab'
                              ? 'bg-blue-50 text-blue-800 border-blue-200'
                              : 'bg-amber-50 text-amber-800 border-amber-200'
                          }`}
                        >
                          {venueLabel}
                        </span>
                        {row.venue === 'schwab' && row.accountNumber && (
                          <span className="text-[10px] text-gray-500">
                            ···{String(row.accountNumber).slice(-4)}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleClosePosition(row)}
                        disabled={closingPosition}
                        className="px-2 py-1 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium flex items-center space-x-1"
                      >
                        {closingPosition && closeTarget === row ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
                            <span>Closing...</span>
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faTimes} className="text-xs" />
                            <span>Close</span>
                          </>
                        )}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                      <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                        <div className="text-[10px] text-gray-600 mb-0.5">Quantity</div>
                        <div className="text-base font-bold text-gray-800">
                          {row.qty > 0 ? '+' : ''}
                          {Number(row.qty).toLocaleString()} shares
                        </div>
                        <div className="text-[10px] text-gray-500 mt-0.5">
                          {row.side === 'long' ? 'Long Position' : 'Short Position'}
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                        <div className="text-[10px] text-gray-600 mb-0.5">Market Value</div>
                        <div className="text-base font-bold text-gray-800">
                          {formatCurrency(row.marketValue)}
                        </div>
                        <div className="text-[10px] text-gray-500 mt-0.5">Current Value</div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                        <div className="text-[10px] text-gray-600 mb-0.5">Cost Basis</div>
                        <div className="text-base font-bold text-gray-800">
                          {formatCurrency(row.costBasis)}
                        </div>
                        <div className="text-[10px] text-gray-500 mt-0.5">Average Cost</div>
                      </div>
                      <div
                        className={`bg-gray-50 rounded-lg p-2 border ${
                          row.unrealizedPl >= 0 ? 'border-green-200' : 'border-red-200'
                        }`}
                      >
                        <div className="text-[10px] text-gray-600 mb-0.5">Unrealized P/L</div>
                        <div
                          className={`text-base font-bold ${
                            row.unrealizedPl >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {formatCurrency(row.unrealizedPl)}
                        </div>
                        <div
                          className={`text-[10px] mt-0.5 ${
                            row.unrealizedPl >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {formatPercent(plPct)}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-600">
                      <span className="font-medium">Current Price:</span>{' '}
                      {formatCurrency(row.currentPrice)}
                      {row.lastdayPrice != null &&
                        row.lastdayPrice !== row.currentPrice && (
                          <span
                            className={`ml-2 ${
                              row.currentPrice >= row.lastdayPrice
                                ? 'text-green-600'
                                : 'text-red-600'
                            }`}
                          >
                            ({row.currentPrice >= row.lastdayPrice ? '+' : ''}
                            {formatCurrency(row.currentPrice - row.lastdayPrice)})
                          </span>
                        )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Mutual fund — Schwab NAV + fundamentals */}
      {isMutualFund && (
        <div className="mb-3">
          <MutualFundPanel symbol={asset.symbol} assetType="MUTUAL_FUND" />
        </div>
      )}

      {/* Price chart — desk-style candlesticks (equity / ETF; funds are EOD NAV) */}
      {asset.tradable && !isMutualFund && (
        <div className="mb-3">
          <HistoricalBarsChart symbol={asset.symbol} height={520} />
        </div>
      )}

      {/* Toast for close / order feedback when dialogs are closed */}
      {orderMessage && !showOrderForm && !showCloseDialog && (
        <div
          className={`mb-3 px-3 py-2 rounded-lg text-xs border ${
            orderMessage.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {orderMessage.text}
        </div>
      )}

      {/* Advanced order ticket */}
      {showOrderForm &&
        asset.tradable &&
        createPortal(
          <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
            <button
              type="button"
              className="fixed inset-0 bg-[#0B0E14]/70"
              aria-label="Close order dialog"
              onClick={resetOrderForm}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-label={`Order ticket ${asset.symbol}`}
              className={`relative z-[1] w-full ${
                orderTicketTab === 'mleg' ? 'sm:max-w-3xl' : 'sm:max-w-xl'
              } max-h-[92vh] overflow-y-auto rounded-t-xl sm:rounded-xl border border-hairline bg-surface shadow-2xl`}
            >
              <div className="sticky top-0 z-10 flex items-center justify-between gap-2 px-4 py-3 border-b border-hairline bg-surface/95 backdrop-blur-md">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted font-semibold">
                    Order ticket
                  </div>
                  <div className="text-base font-bold text-ink flex items-center gap-2 flex-wrap">
                    <FontAwesomeIcon
                      icon={orderSide === 'buy' ? faArrowUp : faArrowDown}
                      className={`text-xs ${orderSide === 'buy' ? 'text-bull' : 'text-bear'}`}
                    />
                    {asset.symbol}
                    {position ? (
                      <span className="text-[11px] font-medium text-muted">
                        Pos {position.qty > 0 ? '+' : ''}
                        {position.qty}
                      </span>
                    ) : null}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={resetOrderForm}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-hairline text-muted hover:bg-elevated hover:text-ink"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-sm" />
                </button>
              </div>

              {!isMutualFund && (
                <div className="px-4 pt-3">
                  <div className="inline-flex rounded-md border border-hairline overflow-hidden text-[11px]">
                    {[
                      { id: 'stock', label: 'Stock' },
                      { id: 'bracket', label: 'Bracket / OTO' },
                      { id: 'mleg', label: 'Multi-leg' },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => {
                          setOrderTicketTab(tab.id);
                          setOrderMessage(null);
                        }}
                        className={`px-3 py-1.5 font-semibold ${
                          orderTicketTab === tab.id
                            ? 'bg-[var(--lt-action)] text-white'
                            : 'text-muted hover:bg-elevated'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="px-4 py-3 space-y-3">
                {orderMessage && (
                  <div
                    className={`p-2 rounded-lg text-xs border ${
                      orderMessage.type === 'success'
                        ? 'bg-green-50 border-green-200 text-green-800'
                        : 'bg-red-50 border-red-200 text-red-800'
                    }`}
                  >
                    {orderMessage.text}
                  </div>
                )}

                {(isMutualFund || orderTicketTab === 'stock') && (
                  <OrderForm
                    symbol={asset.symbol}
                    initialSide={orderSide}
                    lockSymbol
                    hideQuoteStrip={false}
                    defaultAutoRefresh
                    variant="modal"
                    onOrderSubmitted={async (order) => {
                      setOrderMessage({
                        type: 'success',
                        text: `Order submitted${order?.id ? ` · ${order.id}` : ''}`,
                      });
                      await loadPosition();
                      setTimeout(() => {
                        setShowOrderForm(false);
                        setOrderMessage(null);
                      }, 1800);
                    }}
                  />
                )}

                {!isMutualFund && orderTicketTab === 'bracket' && (
                  <form onSubmit={handleBracketSubmit} className="space-y-3">
                    <p className="text-[11px] text-muted leading-snug">
                      Alpaca bracket entry with take-profit (limit) and stop-loss. Uses GTC children.
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-muted mb-0.5">Side</label>
                        <select
                          value={brktSide}
                          onChange={(e) => {
                            setBrktSide(e.target.value);
                            seedBracketFromQuote(e.target.value);
                          }}
                          className="w-full h-9 px-2 text-xs lt-input"
                        >
                          <option value="buy">Buy (long)</option>
                          <option value="sell">Sell (short)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-muted mb-0.5">Qty</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={brktQty}
                          onChange={(e) => setBrktQty(e.target.value)}
                          className="w-full h-9 px-2 text-xs lt-input num"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-muted mb-0.5">Entry</label>
                        <select
                          value={brktEntry}
                          onChange={(e) => setBrktEntry(e.target.value)}
                          className="w-full h-9 px-2 text-xs lt-input"
                        >
                          <option value="market">Market</option>
                          <option value="limit">Limit</option>
                        </select>
                      </div>
                      {brktEntry === 'limit' ? (
                        <div>
                          <label className="block text-xs font-medium text-muted mb-0.5">Limit</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={brktLimit}
                            onChange={(e) => setBrktLimit(e.target.value)}
                            className="w-full h-9 px-2 text-xs lt-input num"
                            required
                          />
                        </div>
                      ) : (
                        <div className="flex items-end">
                          <div className="text-[10px] text-muted pb-2">Fills at market, then attaches TP/SL</div>
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-medium text-muted mb-0.5">Take profit</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={brktTp}
                          onChange={(e) => setBrktTp(e.target.value)}
                          className="w-full h-9 px-2 text-xs lt-input num"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-muted mb-0.5">Stop loss</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={brktSl}
                          onChange={(e) => setBrktSl(e.target.value)}
                          className="w-full h-9 px-2 text-xs lt-input num"
                          required
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={brktSubmitting}
                        className={`flex-1 h-10 rounded-md text-xs font-semibold uppercase tracking-wider disabled:opacity-50 ${
                          brktSide === 'buy' ? 'bg-bull text-[#0B0E14]' : 'bg-bear text-white'
                        }`}
                      >
                        {brktSubmitting ? (
                          <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                        ) : (
                          `Submit bracket ${brktSide}`
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={resetOrderForm}
                        className="px-3 h-10 rounded-md border border-hairline text-xs text-muted hover:bg-elevated"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                {!isMutualFund && orderTicketTab === 'mleg' && (
                  <MultiLegOrderForm
                    symbol={asset.symbol}
                    onOrderSubmitted={async () => {
                      setOrderMessage({ type: 'success', text: 'Multi-leg order submitted' });
                      await loadPosition();
                      setTimeout(() => {
                        setShowOrderForm(false);
                        setOrderMessage(null);
                      }, 1800);
                    }}
                  />
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Market close position dialog */}
      {showCloseDialog &&
        (closeTarget || position) &&
        createPortal(
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 overflow-y-auto">
            <button
              type="button"
              className="fixed inset-0 bg-[#0B0E14]/70"
              aria-label="Dismiss close dialog"
              disabled={closingPosition}
              onClick={() => {
                if (!closingPosition) {
                  setShowCloseDialog(false);
                  setCloseTarget(null);
                }
              }}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-label={`Close ${asset.symbol} position`}
              className="relative z-[1] w-full max-w-sm rounded-xl border border-gray-200 bg-white shadow-2xl"
            >
              <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-100">
                <h2 className="text-base font-bold text-gray-800 flex items-center">
                  <FontAwesomeIcon icon={faTimes} className="mr-1.5 text-xs text-orange-600" />
                  Close {asset.symbol}
                  <span
                    className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${
                      (closeTarget || position)?.venue === 'schwab'
                        ? 'bg-blue-50 text-blue-800 border-blue-200'
                        : 'bg-amber-50 text-amber-800 border-amber-200'
                    }`}
                  >
                    {(closeTarget || position)?.venue === 'schwab' ? 'Schwab' : 'Alpaca'}
                  </span>
                </h2>
                <button
                  type="button"
                  disabled={closingPosition}
                  onClick={() => {
                    setShowCloseDialog(false);
                    setCloseTarget(null);
                  }}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-sm" />
                </button>
              </div>
              <div className="px-4 py-3 space-y-3">
                <p className="text-sm text-gray-700">
                  Submit a <span className="font-semibold">market order</span> to flatten your entire{' '}
                  <span className="font-semibold">{(closeTarget || position).side}</span> position
                  {(closeTarget || position)?.venue === 'schwab' ? ' on Schwab' : ' on Alpaca'}?
                </p>
                <div className="grid grid-cols-2 gap-2 text-[11px] bg-gray-50 border border-gray-100 rounded-lg p-2.5">
                  <div>
                    <div className="text-gray-500">Qty</div>
                    <div className="font-semibold text-gray-800">
                      {(closeTarget || position).qty > 0 ? '+' : ''}
                      {(closeTarget || position).qty}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Market value</div>
                    <div className="font-semibold text-gray-800">
                      {formatCurrency((closeTarget || position).marketValue)}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Avg cost</div>
                    <div className="font-semibold text-gray-800">
                      {formatCurrency((closeTarget || position).costBasis)}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Unrealized P/L</div>
                    <div
                      className={`font-semibold ${
                        (closeTarget || position).unrealizedPl >= 0
                          ? 'text-green-700'
                          : 'text-red-700'
                      }`}
                    >
                      {formatCurrency((closeTarget || position).unrealizedPl)}
                    </div>
                  </div>
                </div>
                {orderMessage?.type === 'error' && (
                  <div className="p-2 rounded-lg text-xs bg-red-50 border border-red-200 text-red-800">
                    {orderMessage.text}
                  </div>
                )}
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={confirmClosePosition}
                    disabled={closingPosition}
                    className="flex-1 px-3 py-2 rounded-lg font-semibold text-xs bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {closingPosition ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1 text-xs" />
                        Closing…
                      </>
                    ) : (
                      'Close at market'
                    )}
                  </button>
                  <button
                    type="button"
                    disabled={closingPosition}
                    onClick={() => {
                      setShowCloseDialog(false);
                      setCloseTarget(null);
                      setOrderMessage(null);
                    }}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-xs font-medium disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Trading Data - Blended Alpaca and Yahoo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
        {/* Market Data Summary */}
        {asset.tradable && marketDataSummary && (
          <div className="bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
              <h3 className="text-base font-bold text-gray-800 flex items-center">
                <FontAwesomeIcon icon={faChartLine} className="mr-1.5 text-xs text-gray-600" />
                Trading Data
                {watchlistMarketData && (
                  <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-[10px] font-semibold rounded">
                    From Watchlist
                  </span>
                )}
              </h3>
            </div>
            <div className="p-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div>
                  <div className="text-xs text-gray-600 mb-1">Open</div>
                  <div className="text-sm font-bold text-gray-800">
                    {formatCurrency(marketDataSummary.open)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">High</div>
                  <div className="text-sm font-bold text-green-600">
                    {formatCurrency(marketDataSummary.high)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">Low</div>
                  <div className="text-sm font-bold text-red-600">
                    {formatCurrency(marketDataSummary.low)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">Close</div>
                  <div className="text-sm font-bold text-gray-800">
                    {formatCurrency(marketDataSummary.close)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">Change</div>
                  <div className={`text-sm font-bold ${
                    marketDataSummary.close && marketDataSummary.open
                      ? (marketDataSummary.close >= marketDataSummary.open ? 'text-green-600' : 'text-red-600')
                      : 'text-gray-600'
                  }`}>
                    {marketDataSummary.close && marketDataSummary.open
                      ? formatCurrency(marketDataSummary.close - marketDataSummary.open)
                      : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">Change %</div>
                  <div className={`text-sm font-bold ${
                    marketDataSummary.close && marketDataSummary.open
                      ? (marketDataSummary.close >= marketDataSummary.open ? 'text-green-600' : 'text-red-600')
                      : 'text-gray-600'
                  }`}>
                    {marketDataSummary.close && marketDataSummary.open
                      ? formatPercent(calculateChange(marketDataSummary.close, marketDataSummary.open))
                      : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">Volume</div>
                  <div className="text-sm font-bold text-gray-800">
                    {formatNumber(marketDataSummary.volume)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Latest Quote / Bid-Ask (equity) or NAV (mutual fund) */}
        {asset.tradable && (quote || watchlistMarketData || (isMutualFund && schwabOverview)) && (
          <div className="bg-white rounded-lg shadow-md p-3 border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-bold text-gray-800 flex items-center">
                <FontAwesomeIcon icon={faDollarSign} className="mr-1.5 text-xs text-gray-600" />
                {isMutualFund ? 'NAV / Quote' : 'Quote'}
              </h3>
              <button
                onClick={loadLatestQuote}
                disabled={loadingQuote}
                className="px-2 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
              >
                {loadingQuote ? (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSync} className="mr-1 text-xs" />
                    Refresh
                  </>
                )}
              </button>
            </div>
            {isMutualFund && quote ? (
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                  <div className="text-xs text-gray-600 mb-0.5">NAV / Last</div>
                  <div className="text-lg font-bold text-gray-800">
                    {formatCurrency(quote.lastPrice ?? quote.bidPrice ?? quote.askPrice)}
                  </div>
                  <div className="text-[10px] text-gray-500">End of day when applicable</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                  <div className="text-xs text-gray-600 mb-0.5">Change</div>
                  <div
                    className={`text-lg font-bold ${
                      Number(quote.netChange) >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {quote.netChange != null ? formatCurrency(quote.netChange) : 'N/A'}
                    {quote.netPercentChange != null
                      ? ` (${Number(quote.netPercentChange) >= 0 ? '+' : ''}${Number(
                          quote.netPercentChange
                        ).toFixed(2)}%)`
                      : ''}
                  </div>
                </div>
                {asset.cusip && (
                  <div className="bg-gray-50 rounded-lg p-2 border border-gray-200 col-span-2">
                    <div className="text-xs text-gray-600 mb-0.5">CUSIP</div>
                    <div className="text-sm font-mono font-semibold text-gray-800">{asset.cusip}</div>
                  </div>
                )}
              </div>
            ) : null}
            {!isMutualFund && quote && !watchlistMarketData ? (
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                  <div className="text-xs text-gray-600 mb-0.5">Bid</div>
                  <div className="text-lg font-bold text-green-600">{formatCurrency(quote.bidPrice)}</div>
                  <div className="text-[10px] text-gray-500">Size: {quote.bidSize?.toLocaleString() || 'N/A'}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                  <div className="text-xs text-gray-600 mb-0.5">Ask</div>
                  <div className="text-lg font-bold text-red-600">{formatCurrency(quote.askPrice)}</div>
                  <div className="text-[10px] text-gray-500">Size: {quote.askSize?.toLocaleString() || 'N/A'}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                  <div className="text-xs text-gray-600 mb-0.5">Spread</div>
                  <div className="text-sm font-bold text-gray-700">
                    {quote.bidPrice && quote.askPrice ? formatCurrency(quote.askPrice - quote.bidPrice) : 'N/A'}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                  <div className="text-xs text-gray-600 mb-0.5">Mid</div>
                  <div className="text-sm font-bold text-gray-800">
                    {quote.bidPrice && quote.askPrice
                      ? formatCurrency((quote.bidPrice + quote.askPrice) / 2)
                      : 'N/A'}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Company Information & Financial Metrics - Blended Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
        {/* Company Profile */}
        {yahooData?.summaryProfile && (
          <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-3 border border-gray-100">
            <h2 className="text-base font-bold text-gray-800 mb-3">Company Information</h2>
            <div className="space-y-2">
              {yahooData.summaryProfile.longBusinessSummary && (
                <div>
                  <p className="text-sm text-gray-800 leading-relaxed">
                    {yahooData.summaryProfile.longBusinessSummary}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-200">
                {yahooData.summaryProfile.sector && (
                  <div>
                    <div className="text-xs text-gray-600 mb-0.5">Sector</div>
                    <div className="text-sm font-medium text-gray-800">{yahooData.summaryProfile.sector}</div>
                  </div>
                )}
                {yahooData.summaryProfile.industry && (
                  <div>
                    <div className="text-xs text-gray-600 mb-0.5">Industry</div>
                    <div className="text-sm font-medium text-gray-800">{yahooData.summaryProfile.industry}</div>
                  </div>
                )}
                {yahooData.summaryProfile.country && (
                  <div>
                    <div className="text-xs text-gray-600 mb-0.5">Country</div>
                    <div className="text-sm font-medium text-gray-800">{yahooData.summaryProfile.country}</div>
                  </div>
                )}
                {yahooData.summaryProfile.fullTimeEmployees && (
                  <div>
                    <div className="text-xs text-gray-600 mb-0.5">Employees</div>
                    <div className="text-sm font-medium text-gray-800">{formatNumber(yahooData.summaryProfile.fullTimeEmployees)}</div>
                  </div>
                )}
                {yahooData.summaryProfile.website && (
                  <div className="col-span-2">
                    <div className="text-xs text-gray-600 mb-0.5">Website</div>
                    <a href={yahooData.summaryProfile.website} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline">
                      {yahooData.summaryProfile.website}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Key Financial Metrics */}
        <div className="bg-white rounded-lg shadow-md p-3 border border-gray-100">
          <h2 className="text-base font-bold text-gray-800 mb-3">Key Metrics</h2>
          {loadingYahoo ? (
            <div className="flex items-center text-xs text-gray-500 py-4">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
              Loading metrics…
            </div>
          ) : yahooError && keyMetricRows.length === 0 ? (
            <p className="text-xs text-gray-500 py-2">{yahooError}</p>
          ) : keyMetricRows.length === 0 ? (
            <p className="text-xs text-gray-500 py-2">No key metrics available</p>
          ) : (
            <div className="space-y-3">
              {keyMetricRows.map((row) => (
                <div key={row.label}>
                  <div className="text-xs text-gray-600 mb-0.5">{row.label}</div>
                  <div className="text-sm font-bold text-gray-800">{row.value}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Financial Health */}
      {yahooData?.financialData && (
        <div className="bg-white rounded-lg shadow-md p-3 border border-gray-100 mb-3">
          <h2 className="text-base font-bold text-gray-800 mb-3">Financial Health</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {yahooData.financialData.totalCash && (
              <div>
                <div className="text-xs text-gray-600 mb-0.5">Total Cash</div>
                <div className="text-sm font-bold text-gray-800">
                  {formatNumber(yahooRaw(yahooData.financialData.totalCash) ?? yahooData.financialData.totalCash)}
                </div>
              </div>
            )}
            {yahooData.financialData.totalDebt && (
              <div>
                <div className="text-xs text-gray-600 mb-0.5">Total Debt</div>
                <div className="text-sm font-bold text-gray-800">
                  {formatNumber(yahooRaw(yahooData.financialData.totalDebt) ?? yahooData.financialData.totalDebt)}
                </div>
              </div>
            )}
            {(yahooFmt(yahooData.financialData.returnOnAssets, { percent: true })) && (
              <div>
                <div className="text-xs text-gray-600 mb-0.5">ROA</div>
                <div className="text-sm font-bold text-gray-800">
                  {yahooFmt(yahooData.financialData.returnOnAssets, { percent: true })}
                </div>
              </div>
            )}
            {(yahooFmt(yahooData.financialData.returnOnEquity, { percent: true })) && (
              <div>
                <div className="text-xs text-gray-600 mb-0.5">ROE</div>
                <div className="text-sm font-bold text-gray-800">
                  {yahooFmt(yahooData.financialData.returnOnEquity, { percent: true })}
                </div>
              </div>
            )}
            {(yahooFmt(yahooData.financialData.profitMargins, { percent: true })) && (
              <div>
                <div className="text-xs text-gray-600 mb-0.5">Profit Margin</div>
                <div className="text-sm font-bold text-gray-800">
                  {yahooFmt(yahooData.financialData.profitMargins, { percent: true })}
                </div>
              </div>
            )}
            {(yahooFmt(yahooData.financialData.operatingMargins, { percent: true })) && (
              <div>
                <div className="text-xs text-gray-600 mb-0.5">Operating Margin</div>
                <div className="text-sm font-bold text-gray-800">
                  {yahooFmt(yahooData.financialData.operatingMargins, { percent: true })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Insider trading & ownership (Yahoo) */}
      {hasOwnershipBlock && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
          <div className="bg-white rounded-lg shadow-md p-3 border border-gray-100">
            <h2 className="text-base font-bold text-gray-800 mb-2">Ownership</h2>
            <div className="space-y-2 text-sm">
              {yahooFmt(majorHolders.insidersPercentHeld, { percent: true }) && (
                <div className="flex justify-between gap-2">
                  <span className="text-xs text-gray-600">Insiders</span>
                  <span className="font-medium text-gray-800 tabular-nums">
                    {yahooFmt(majorHolders.insidersPercentHeld, { percent: true })}
                  </span>
                </div>
              )}
              {yahooFmt(majorHolders.institutionsPercentHeld, { percent: true }) && (
                <div className="flex justify-between gap-2">
                  <span className="text-xs text-gray-600">Institutions</span>
                  <span className="font-medium text-gray-800 tabular-nums">
                    {yahooFmt(majorHolders.institutionsPercentHeld, { percent: true })}
                  </span>
                </div>
              )}
              {yahooFmt(majorHolders.institutionsFloatPercentHeld, { percent: true }) && (
                <div className="flex justify-between gap-2">
                  <span className="text-xs text-gray-600">Inst. float</span>
                  <span className="font-medium text-gray-800 tabular-nums">
                    {yahooFmt(majorHolders.institutionsFloatPercentHeld, { percent: true })}
                  </span>
                </div>
              )}
              {(yahooRaw(majorHolders.institutionsCount) != null ||
                majorHolders.institutionsCount?.fmt) && (
                <div className="flex justify-between gap-2 pt-1 border-t border-gray-100">
                  <span className="text-xs text-gray-600"># Institutions</span>
                  <span className="font-medium text-gray-800 tabular-nums">
                    {majorHolders.institutionsCount?.fmt ||
                      formatNumber(yahooRaw(majorHolders.institutionsCount))}
                  </span>
                </div>
              )}
            </div>

            {insiderHolderRows.length > 0 && (
              <div className="mt-3 pt-2 border-t border-gray-100">
                <div className="text-xs font-semibold text-gray-700 mb-1.5">Key insiders</div>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {insiderHolderRows.map((h) => (
                    <div key={h.id} className="text-[11px]">
                      <div className="font-medium text-gray-800 truncate">{h.name}</div>
                      <div className="text-gray-500 flex justify-between gap-2">
                        <span className="truncate">{h.relation}</span>
                        <span className="tabular-nums shrink-0">
                          {h.position != null ? formatNumber(h.position) : h.dateLabel}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-3 border border-gray-100">
            <div className="flex items-baseline justify-between gap-2 mb-2">
              <h2 className="text-base font-bold text-gray-800">Insider transactions</h2>
              <span className="text-[10px] text-gray-500">Yahoo / Form 4 style filings</span>
            </div>
            {insiderTxRows.length === 0 ? (
              <p className="text-xs text-gray-500 py-2">No recent insider transactions available</p>
            ) : (
              <div className="overflow-x-auto max-h-72 overflow-y-auto">
                <table className="w-full text-[11px]">
                  <thead className="sticky top-0 bg-white">
                    <tr className="text-gray-500 border-b border-gray-100">
                      <th className="text-left font-medium py-1 pr-2">Date</th>
                      <th className="text-left font-medium py-1 px-1">Insider</th>
                      <th className="text-left font-medium py-1 px-1">Role</th>
                      <th className="text-left font-medium py-1 px-1">Type</th>
                      <th className="text-right font-medium py-1 px-1">Shares</th>
                      <th className="text-right font-medium py-1 pl-1">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {insiderTxRows.map((tx) => (
                      <tr key={tx.id} className="border-b border-gray-50 last:border-0 align-top">
                        <td className="py-1.5 pr-2 text-gray-600 whitespace-nowrap">{tx.dateLabel}</td>
                        <td className="py-1.5 px-1 text-gray-800 font-medium max-w-[140px]">
                          <div className="truncate" title={tx.filer}>
                            {tx.filer}
                          </div>
                        </td>
                        <td className="py-1.5 px-1 text-gray-500 max-w-[100px]">
                          <div className="truncate" title={tx.relation}>
                            {tx.relation}
                          </div>
                        </td>
                        <td className="py-1.5 px-1">
                          <span
                            className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                              tx.side === 'Buy'
                                ? 'bg-green-50 text-green-700'
                                : tx.side === 'Sell'
                                  ? 'bg-red-50 text-red-700'
                                  : 'bg-gray-50 text-gray-600'
                            }`}
                            title={tx.text}
                          >
                            {tx.side === 'Other' ? (tx.text !== '—' ? tx.text.slice(0, 24) : '—') : tx.side}
                          </span>
                        </td>
                        <td className="py-1.5 px-1 text-right tabular-nums text-gray-800">
                          {tx.shares != null ? formatNumber(tx.shares) : '—'}
                        </td>
                        <td className="py-1.5 pl-1 text-right tabular-nums text-gray-700">
                          {tx.value != null ? formatCurrency(tx.value) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {institutionRows.length > 0 && (
              <div className="mt-3 pt-2 border-t border-gray-100">
                <div className="text-xs font-semibold text-gray-700 mb-1.5">Top institutions</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="text-gray-500 border-b border-gray-100">
                        <th className="text-left font-medium py-1 pr-2">Holder</th>
                        <th className="text-right font-medium py-1 px-1">%</th>
                        <th className="text-right font-medium py-1 px-1">Shares</th>
                        <th className="text-right font-medium py-1 pl-1">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {institutionRows.map((row) => (
                        <tr key={row.id} className="border-b border-gray-50 last:border-0">
                          <td className="py-1 pr-2 text-gray-800 font-medium max-w-[180px]">
                            <div className="truncate" title={row.org}>
                              {row.org}
                            </div>
                          </td>
                          <td className="py-1 px-1 text-right tabular-nums text-gray-700">
                            {row.pct != null ? `${(row.pct * 100).toFixed(2)}%` : '—'}
                          </td>
                          <td className="py-1 px-1 text-right tabular-nums text-gray-600">
                            {row.shares != null ? formatNumber(row.shares) : '—'}
                          </td>
                          <td className="py-1 pl-1 text-right tabular-nums text-gray-600">
                            {row.value != null ? formatCurrency(row.value) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Earnings & Analyst Recommendations */}
      {(hasEarningsBlock || yahooData?.recommendationTrend) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
          {hasEarningsBlock && (
            <div className="bg-white rounded-lg shadow-md p-2.5 border border-gray-100">
              <div className="flex items-baseline justify-between gap-2 mb-2">
                <h2 className="text-sm font-bold text-gray-800">Earnings</h2>
                {nextEarningsDate && (
                  <div className="text-[11px] text-amber-800 font-medium text-right">
                    Next {formatEarningsDay(nextEarningsDate)}
                    {nextEarningsIsEstimate ? ' (est)' : ''}
                    {nextEpsEstimate != null ? ` · EPS $${Number(nextEpsEstimate).toFixed(2)}` : ''}
                  </div>
                )}
              </div>

              {quarterlyEarnings.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="text-gray-500 border-b border-gray-100">
                        <th className="text-left font-medium py-1 pr-2">Quarter</th>
                        <th className="text-right font-medium py-1 px-1">Reported</th>
                        <th className="text-right font-medium py-1 px-1">EPS</th>
                        <th className="text-right font-medium py-1 px-1">Est</th>
                        <th className="text-right font-medium py-1 pl-1">Surprise</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quarterlyEarnings.map((q) => {
                        const surprise = q.surpriseN;
                        const surpriseTone =
                          surprise == null
                            ? 'text-gray-700'
                            : surprise >= 0
                              ? 'text-green-700'
                              : 'text-red-700';
                        return (
                          <tr key={`${q.date}-${q.sortMs}`} className="border-b border-gray-50 last:border-0">
                            <td className="py-1 pr-2 text-gray-700 font-medium">{q.date || '—'}</td>
                            <td className="py-1 px-1 text-right text-gray-500 tabular-nums">
                              {q.reportedDate
                                ? new Date(q.reportedDate).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: '2-digit',
                                  })
                                : '—'}
                            </td>
                            <td className="py-1 px-1 text-right text-gray-800 tabular-nums">
                              {q.actualN != null ? `$${q.actualN.toFixed(2)}` : '—'}
                            </td>
                            <td className="py-1 px-1 text-right text-gray-600 tabular-nums">
                              {q.estimateN != null ? `$${q.estimateN.toFixed(2)}` : '—'}
                            </td>
                            <td className={`py-1 pl-1 text-right tabular-nums font-medium ${surpriseTone}`}>
                              {surprise == null
                                ? '—'
                                : `${surprise > 0 ? '+' : ''}${surprise.toFixed(1)}%`}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : yearlyFinancials.length > 0 ? (
                <div className="space-y-1">
                  {yearlyFinancials.map((year) => (
                    <div key={year.date} className="flex justify-between text-[11px]">
                      <span className="text-gray-500">{year.date}</span>
                      <span className="text-gray-800 font-medium tabular-nums">
                        Rev {formatNumber(year.revenue)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-gray-500">No recent quarterly history.</p>
              )}
            </div>
          )}

          {/* Analyst Recommendations */}
          {yahooData.recommendationTrend && yahooData.recommendationTrend.trend && yahooData.recommendationTrend.trend.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-2.5 border border-gray-100">
              <h2 className="text-sm font-bold text-gray-800 mb-2">Analyst Recommendations</h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-1.5">
                {yahooData.recommendationTrend.trend.map((rec, idx) => (
                  <div key={idx} className="text-center p-1.5 bg-gray-50 rounded">
                    <div className="text-[10px] text-gray-600 mb-0.5">{rec.period}</div>
                    <div className="text-sm font-bold text-gray-800">{rec.strongBuy || 0}</div>
                    <div className="text-[9px] text-gray-500">Strong Buy</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Corporate Actions */}
      {asset.tradable && (
        <div className="mb-3">
          <CorporateActions symbol={asset.symbol} />
        </div>
      )}

      {/* News */}
      <div className="mb-3">
        <TickerNews symbol={asset.symbol} />
      </div>
    </main>
  );
};

export default AssetDetailsPage;


